import Dolly, { addShare } from '@dolly/core';
import type { PluginFunction } from '@kastjs/runtime-core';
import {
  createCoreIdWrap,
  getChildAppList,
  getConfigFromLocalstorage,
  replaceCdnDomain,
  writeConfigToLocalstorage,
} from './utils';
import { IDollyItem, IDollyOptions, MicroAppEntryConfigs } from './types';
import React from 'react';

// export const rootMicroContainerId = 'micro-apps-root-container';
const paramsConfigMap = {};
const appContainerId = (basename: string) => 'dolly-container' + basename?.replace(/\//g, '-');

// 转化成dolly需要的配置
const convertToDolly = (list: IDollyItem[], originBasename: string): MicroAppEntryConfigs => {
  const createCoreId = createCoreIdWrap(list.map(item => item.appId || 0));
  return list.reduce((configs: MicroAppEntryConfigs, dolly) => {
    if (!dolly.basename || dolly.basename.length === 0) {
      console.warn('未指定 basename ，将无法命中dolly子工程激活规则');
    }

    const basenames = (Array.isArray(dolly.basename) ? dolly.basename : [dolly.basename]).map(
      url => originBasename + url
    );

    let appId = dolly?.appId || createCoreId();
    let params = {
      ...dolly?.customProps,
      basename: basenames,
      renderId: dolly.renderId,
      renderNode: () => {
        return document.querySelector(dolly.renderId);
      },
    };

    let appName = (originBasename ? `${originBasename}_${dolly.appName}` : dolly.appName).replace(
      /\//g,
      ''
    );

    configs[appId] = {
      appId,
      appName,
      rule: `return ${basenames
        .map(item => `location.pathname.startsWith('${item}')`)
        .join(' || ')} ? true : false`,
      jsEntry: dolly!.jsEntry,
      cssEntry: dolly?.cssEntry || [],
      disableCssSandbox: true,
      disableJsSandbox: false,
      params,
    };

    paramsConfigMap[appName] = params;
    return configs;
  }, {});
};

// 初始化dolly
const _initDolly = async (dollyConfigs: MicroAppEntryConfigs, options?: IDollyOptions) => {
  // const needRuntime = options?.enabledRuntime === true;
  const dolly = await Dolly.init({
    env: 'development',
    // env: options?.env || 'development',
    // 应用的运行环境标识，映射为上线单中的生效环境字段，默认取当前域名，非必传
    identifyKey: options?.identifyKey,
    // 主应用id
    coreId: options?.appId || 9999,
    // 本地自定义的子应用配置
    customerConfig: dollyConfigs,
    customProps: {
      // 需要传递给子应用的数据或者属性
      globalProps: options && options.globalProps ? options.globalProps : {},
      createParamsConfig(appName: string) {
        const params = paramsConfigMap[appName] || {};
        if (Array.isArray(params.basename)) {
          return {
            ...params,
            basename: params.basename.find(item => location.pathname.startsWith(item)),
          };
        }
        return params;
      },
    },
    prefetch: () => {
      return options?.prefetch || false;
    },
    configHandler: appConfigs => {
      console.log(appConfigs, 'appConfigs~~~~~');
      // 对从接口拿到的config进行处理
      Object.keys(appConfigs).forEach(key => {
        if (dollyConfigs.hasOwnProperty(key)) {
          appConfigs[key] = {
            ...appConfigs[key],
            disableCssSandbox: dollyConfigs[key].disableCssSandbox,
            disableJsSandbox: dollyConfigs[key].disableJsSandbox,
            appName: dollyConfigs[key].appName,
            rule: dollyConfigs[key].rule,
          };
        }
      });
      return appConfigs;
    },
  });

  return dolly;
};

/**
 *  1. 收集dolly节点
 *  2. 节点信息分发至dolly, 初始化dolly
 */
const startDolly: PluginFunction<IDollyOptions> = async (context, options) => {
  return new Promise(async (resolve, reject) => {
    const { appSettings, addWrapper } = context;
    const { type, share, appId } = options;
    // 针对子应用嵌套场景 需要拼接basename
    let originBasename = appSettings.router?.options?.basename || '';

    // 子应用 & 混合应用
    if (type === 'child' || type === 'mix') {
      const { parentProps = {} } = options;
      if (!('createParamsConfig' in parentProps)) {
        throw new Error('获取主应用传递属性失败');
      }
      const { createParamsConfig, name } = parentProps;

      const appInfo = createParamsConfig(name);
      const containerId = appInfo.renderId || appContainerId(appInfo.basename);

      appSettings.rootSelector = '#' + containerId;
      appSettings.router = {
        ...appSettings.router,
        options: {
          basename: appInfo.basename,
        },
      };
      originBasename = appSettings.router?.options?.basename;
      type === 'child' && resolve(context);
    }

    // 主应用 & 混合应用
    if (type === 'main' || type === 'mix') {
      let appList = [];
      // enabledRuntime设置为false，则不走运行时实时计算应用关系逻辑
      if (!options?.enabledRuntime) {
        if (options?.appList && options.appList.length) {
          appList = replaceCdnDomain(options.appList);
          writeConfigToLocalstorage(appList, appId);
        } else {
          appList = getConfigFromLocalstorage(appId);
        }
      } else {
        try {
          const res: any = await getChildAppList(appSettings.routes || [], options);
          if (res && res.result === 1 && res.data && res.data.length) {
            appList = replaceCdnDomain(res.data);
            if (appList.length < getConfigFromLocalstorage(appId).length) {
              console.warn('接口获取到的子应用个数少于本地缓存存储的子应用个数');
            }
            // 财神CRM场景
            if (type === 'main' && options?.source === 'crm') {
              const changeBasename = options?.changeBasename;
              if (changeBasename && typeof changeBasename === 'function') {
                appList = changeBasename(appList);
              }
            }
            writeConfigToLocalstorage(appList, appId);
            console.log(res?.message || '运行时应用关系获取成功');
          } else {
            console.error(res?.message || '运行时应用关系获取失败');
            appList = getConfigFromLocalstorage(appId);
            console.log('get MicroApp relations from localStorage');
          }
        } catch (error) {
          console.error('运行时应用关系获取失败');
          appList = getConfigFromLocalstorage(appId);
          console.log('get MicroApp config from local');
        }
      }
      appList = appList.map(app => {
        return {
          ...app,
          basename: app.appBasename,
        };
      });
      if (!appList || appList.length === 0) {
        resolve(context);
        return;
      }
      // 转换成dolly配置
      const dollyConfig = convertToDolly(appList, originBasename || '');
      const dollyList = [];
      // 根据子应用配置，生成对应的子应用容器
      for (let key in dollyConfig) {
        const item = dollyConfig[key];
        item.params.basename?.forEach(name =>
          dollyList.push({
            appName: item.appName,
            containerId: appContainerId(name),
          })
        );
      }
      addWrapper(props => {
        return (
          <>
            {/* <div id={rootMicroContainerId}> */}
            {/* </div> */}
            {props.children}
            {dollyList.map(item => {
              return <div key={item.containerId} id={item.containerId}></div>;
            })}
          </>
        );
      });

      // 防止重复注册
      if (type === 'mix') {
        const { parentProps = {} } = options;
        const { name } = parentProps;
        const registeredKey = `${name}-registered`;
        if ((window as any)[registeredKey]) {
          resolve(context);
          return;
        }
        Object.defineProperty(window, registeredKey, {
          configurable: false,
          value: true,
        });
      }

      const dolly = await _initDolly(dollyConfig, options);

      // 依赖包挂载window
      share && addShare(share);

      // 启动
      dolly.start();

      resolve(context);
    }
  });
};

export type { IDollyOptions, DollyGlobalProps } from './types';
export { appendCssLink, removeCssLink, replaceCdnDomain } from './utils';
export { SharedModuleMap, getSharedModule } from './share';

export default startDolly;
